import { isValidElement, JSX } from 'react';
/**
 * Utilities copied from Shopify’s design system. Since <FormLayout /> is adapted from Shopify component this
 * uitilities are needed for component to work correctly
 * https://github.com/Shopify/polaris/blob/main/polaris-react/src/utilities/components.tsx
 */

// Wraps `element` in `Component`, if it is not already an instance of
// `Component`. If `props` is passed, those will be added as props on the
// wrapped component. If `element` is null, the component is not wrapped.
export function wrapWithComponent<TProps extends React.PropsWithChildren>(
    element: React.ReactNode | null | undefined,
    Component: React.ComponentType<TProps>,
    props: TProps & JSX.IntrinsicAttributes,
    index?: number
): React.ReactNode {
    if (element == null) {
        return null;
    }

    return isElementOfType(element, Component) ? element : <Component {...props}>{element}</Component>;
}

// In development, we compare based on the name of the function because
// React Hot Loader proxies React components in order to make updates. In
// production we can simply compare the components for equality.
const isComponent =
    process?.env?.NODE_ENV === 'development'
        ? hotReloadComponentCheck
        : (AComponent: React.ComponentType<any>, AnotherComponent: React.ComponentType<any>) =>
              AComponent === AnotherComponent;

// Checks whether `element` is a React element of type `Component` (or one of
// the passed components, if `Component` is an array of React components).
export function isElementOfType<TProps>(
    element: React.ReactNode | null | undefined,
    Component: React.ComponentType<TProps> | React.ComponentType<TProps>[]
): boolean {
    if (element == null || !isValidElement(element) || typeof element.type === 'string') {
        return false;
    }

    const { type: defaultType } = element;
    // Type override allows components to bypass default wrapping behavior. Ex: Stack, ResourceList...
    // See https://github.com/Shopify/app-extension-libs/issues/996#issuecomment-710437088
    const overrideType = (element.props as { __type__?: any }).__type__;
    const type = overrideType || defaultType;
    const Components = Array.isArray(Component) ? Component : [Component];

    return Components.some((AComponent) => typeof type !== 'string' && isComponent(AComponent, type));
}

function hotReloadComponentCheck(AComponent: React.ComponentType<any>, AnotherComponent: React.ComponentType<any>) {
    const componentName = AComponent.name;
    const anotherComponentName = (AnotherComponent as React.FunctionComponent<any>).displayName;

    return AComponent === AnotherComponent || (Boolean(componentName) && componentName === anotherComponentName);
}
